import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://oopbuyspread.wiki';

const sicherheitJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Ist OOPBuy sicher? – Der vollständige Sicherheits-Guide',
  url: `${SITE_URL}/oopbuy-sicherheit`,
  description:
    'Der vollständige Sicherheits-Guide für deinen geschützten Einkauf auf OOPBuy. Erfahre alles über Zahlungssicherheit, Verkäuferprüfung und Betrugsprävention.',
  isPartOf: {
    '@type': 'WebSite',
    name: 'OOPBuy Spreadsheet',
    url: SITE_URL,
  },
  inLanguage: 'de',
};

const securityChecklist = [
  {
    icon: 'ri-lock-line',
    title: 'Keine Passwörter teilen',
    desc: 'Gib dein OOPBuy-Passwort niemals an Dritte weiter – auch nicht an vermeintliche Verkäufer oder Agenten. Offizielle Mitarbeiter werden dich nie nach deinem Passwort fragen.',
  },
  {
    icon: 'ri-bank-card-line',
    title: 'Nur sichere Zahlungsmethoden verwenden',
    desc: 'Nutze ausschließlich die offiziellen Zahlungsmethoden der Plattform: PayPal, Kreditkarte oder die integrierten OOPBuy-Zahlungsoptionen. Vermeide Direktüberweisungen an Verkäufer.',
  },
  {
    icon: 'ri-user-star-line',
    title: 'Verkäufer prüfen',
    desc: 'Überprüfe Verkäufer-Bewertungen, Transaktionsanzahl und Store-Alter. Ein gutes OOPBuy Spreadsheet enthält oft Verkäuferbewertungen direkt in der Tabelle.',
  },
  {
    icon: 'ri-image-line',
    title: 'QC Fotos kontrollieren',
    desc: 'Quality Check Fotos sind dein wichtigstes Werkzeug. Sie zeigen das tatsächliche Produkt aus dem Lager. Vergleiche sie mit den Verkaufsbildern und achte auf Abweichungen.',
  },
  {
    icon: 'ri-truck-line',
    title: 'Versandoptionen vergleichen',
    desc: 'Verschiedene Versandlinien haben unterschiedliche Sicherheitsprofile. Informiere dich über Zollbestimmungen und wähle eine für dein Land optimierte Versandmethode.',
  },
  {
    icon: 'ri-shield-user-line',
    title: 'Datenschutz beachten',
    desc: 'Verwende eine separate E-Mail-Adresse für dein OOPBuy-Konto. Aktiviere die Zwei-Faktor-Authentifizierung, falls verfügbar, und halte deine persönlichen Daten minimal.',
  },
  {
    icon: 'ri-error-warning-line',
    title: 'Betrugsversuche erkennen',
    desc: 'Sei vorsichtig bei Angeboten, die zu gut erscheinen um wahr zu sein. Ungewöhnlich niedrige Preise, Druck zu schnellen Entscheidungen oder Kontaktaufnahme außerhalb der Plattform sind Warnsignale.',
  },
  {
    icon: 'ri-file-list-3-line',
    title: 'Dokumentation aufbewahren',
    desc: 'Speichere alle Bestellbestätigungen, Chat-Verläufe und QC-Fotos. Diese Dokumentation ist im Streitfall deine beste Absicherung.',
  },
];

const comparisonData = [
  { plattform: 'OOPBuy', spreadsheet: '⭐⭐⭐⭐⭐', qcFotos: '⭐⭐⭐⭐⭐', versand: '⭐⭐⭐⭐', benutzerfreundlich: '⭐⭐⭐⭐⭐', sicherheit: '⭐⭐⭐⭐' },
  { plattform: 'Alternative A', spreadsheet: '⭐⭐⭐', qcFotos: '⭐⭐⭐', versand: '⭐⭐⭐', benutzerfreundlich: '⭐⭐⭐', sicherheit: '⭐⭐⭐' },
  { plattform: 'Alternative B', spreadsheet: '⭐⭐⭐⭐', qcFotos: '⭐⭐⭐⭐', versand: '⭐⭐⭐', benutzerfreundlich: '⭐⭐⭐⭐', sicherheit: '⭐⭐⭐' },
  { plattform: 'Alternative C', spreadsheet: '⭐⭐', qcFotos: '⭐⭐', versand: '⭐⭐⭐⭐', benutzerfreundlich: '⭐⭐', sicherheit: '⭐⭐⭐⭐' },
];

export default function SicherheitPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(sicherheitJsonLd) }}
      />
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="w-full px-6 md:px-10">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="font-heading text-xl font-bold text-foreground-900 tracking-tight">
              oopbuy<span className="text-primary-500">spread</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap">
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>
                  Startseite
                </span>
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-4 py-2 text-sm font-semibold bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      </nav>

      <main>
        <section className="relative w-full py-20 md:py-24 px-6 md:px-10 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20cybersecurity%20shield%20concept%20on%20clean%20light%20surface%20soft%20natural%20lighting%20minimal%20tech%20security%20aesthetic%20warm%20neutral%20tones%20abstract%20geometric%20protection%20pattern%20clean%20professional%20composition%20trust%20and%20safety%20theme&width=1920&height=900&seq=security-hero&orientation=landscape"
            alt="OOPBuy Sicherheit"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/45"></div>
          <div className="relative z-10 max-w-6xl mx-auto text-center">
            <span className="inline-block px-4 py-1.5 mb-5 text-xs font-semibold tracking-wider uppercase bg-accent-500/90 text-background-50 rounded-full">
              Sicherheit
            </span>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-4">
              Ist OOPBuy sicher?
            </h1>
            <p className="text-sm md:text-lg text-background-200/90 max-w-2xl mx-auto">
              Der vollständige Sicherheits-Guide für deinen geschützten Einkauf. Erfahre alles über Zahlungssicherheit, Verkäuferprüfung und Betrugsprävention.
            </p>
          </div>
        </section>

        <section className="w-full py-14 md:py-18 px-6 md:px-10 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="bg-background-50 border border-accent-300/60 rounded-lg p-6 md:p-8 mb-10">
              <div className="flex items-start gap-4">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0 mt-1">
                  <i className="ri-information-line text-xl"></i>
                </span>
                <div>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2">Wichtiger Hinweis</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    Diese Website ist eine <strong>unabhängige Informationsplattform</strong> und nicht die offizielle OOPBuy Website. Wir bieten Orientierungshilfen und Empfehlungen basierend auf Community-Erfahrungen, ersetzen aber nicht deine eigene Prüfung vor dem Kauf. Alle hier genannten Sicherheitstipps dienen ausschließlich der Information.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">
              Sicherheits-Checkliste
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {securityChecklist.map((item, i) => (
                <div key={i} className="group p-5 md:p-6 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-300 transition-all duration-200">
                  <span className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors duration-200 mb-4">
                    <i className={`${item.icon} text-xl`}></i>
                  </span>
                  <h4 className="font-heading text-base font-bold text-foreground-900 mb-2">{item.title}</h4>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-18 px-6 md:px-10 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">
              OOPBuy vs. Alternativen – Sicherheitsvergleich
            </h2>
            <div className="overflow-x-auto rounded-lg border border-background-200/70">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-secondary-100">
                    <th className="px-5 py-3.5 text-sm font-semibold text-foreground-800">Plattform</th>
                    <th className="px-5 py-3.5 text-sm font-semibold text-foreground-800">Spreadsheet</th>
                    <th className="px-5 py-3.5 text-sm font-semibold text-foreground-800">QC Fotos</th>
                    <th className="px-5 py-3.5 text-sm font-semibold text-foreground-800">Versand</th>
                    <th className="px-5 py-3.5 text-sm font-semibold text-foreground-800">UX</th>
                    <th className="px-5 py-3.5 text-sm font-semibold text-foreground-800">Sicherheit</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((row, i) => (
                    <tr key={i} className={`border-b border-background-200/50 ${row.plattform === 'OOPBuy' ? 'bg-primary-50/60' : 'bg-background-50'}`}>
                      <td className="px-5 py-3.5 text-sm font-medium text-foreground-800">
                        {row.plattform === 'OOPBuy' && (
                          <span className="inline-block w-2 h-2 rounded-full bg-primary-500 mr-2"></span>
                        )}
                        {row.plattform}
                      </td>
                      <td className="px-5 py-3.5 text-sm">{row.spreadsheet}</td>
                      <td className="px-5 py-3.5 text-sm">{row.qcFotos}</td>
                      <td className="px-5 py-3.5 text-sm">{row.versand}</td>
                      <td className="px-5 py-3.5 text-sm">{row.benutzerfreundlich}</td>
                      <td className="px-5 py-3.5 text-sm">{row.sicherheit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-18 px-6 md:px-10 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">
              Verwandte Kategorien
            </h2>
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((cat, i) => (
                <a
                  key={i}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-all duration-200 cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                    <i className={cat.icon}></i>
                  </span>
                  {cat.name}
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-18 px-6 md:px-10 bg-background-50">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
              Bereit für sicheres Einkaufen?
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto mb-6">
              Mit diesen Sicherheitstipps und einem guten OOPBuy Spreadsheet kannst du selbstbewusst und geschützt einkaufen.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-3.5 text-base font-semibold bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Sicher einkaufen
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-shield-check-line text-sm"></i>
                </span>
              </a>
              <Link
                to="/oopbuy-spreadsheet-guide"
                className="inline-flex items-center gap-2 px-8 py-3.5 text-base font-semibold border-2 border-primary-200 text-primary-700 rounded-md hover:bg-primary-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Vollständigen Guide lesen
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="w-full bg-background-900 text-background-200 py-10 px-6 md:px-10">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="font-heading text-lg font-bold text-background-50">
            oopbuy<span className="text-accent-400">spread</span>
          </Link>
          <p className="text-xs text-background-500">
            &copy; 2026 oopbuyspread.wiki – Unabhängige Informationsplattform
          </p>
        </div>
      </footer>
    </div>
  );
}